import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { ICreateReportModel } from '@models/document/actionsExecution/ICreateReportModel';
import { IOption } from '@srm/ui/types/IOption';
import { IForms, IRows } from '@models/Forms/IForms';
import { IField, IFieldElem, IFormData } from '@models/IFormData';
import { IDocumentData } from '@models/document/IDocumentData';
import { DocumentExecutionService } from '@services/actions/DocumentExecutionService';
import { DocumentService } from '@services/DocumentService';
import FormControl from '@srm/ui/controls/FormControl';
import Select from '@srm/ui/select';
import { ICreateReportExecutorProps } from './ICreateReportExecutorProps';
import ModalFormBuilder, { IModalFormBuilderProps } from '@molecules/ModalFormBuilder/ModalFormBuilder';
import { convertListToMap, getEmptyFormDataByScheme, getFormDataByScheme } from '@utils/documentUtils';
import { ValidatorManager } from '@utils/managers/ValidatorManager';
import { sendModalMsg } from '@srm/ui/lib/modalMessages';
import { useUnsavedFormInput } from '@srm/ui/hooks/useUnsavedFormInput';
import BackgroundJobWait from '@molecules/BackgroundJobWait/BackgroundJobWait';
import { backgroundJobModalProps } from '@molecules/BackgroundJobWait/backgroundJobWaitModal';
import { useBackgroundJobRun } from '@molecules/BackgroundJobWait/useBackgroundJobRun';
import {
    createBackgroundReportTask,
    createReportErrorText,
    CreateReportTask,
    downloadReport,
    ICreateReportRequest,
} from './createReportTask';

const hasFormRows = (form?: IForms): boolean => !!form?.view?.rows?.row?.length;

// FormBuilder залипает на Preloader при rows === undefined; для отчёта без
// пользовательской формы (бэк может вернуть пустую схему) отдаём пустые строки.
const EMPTY_ROWS = { rows: { row: [] } } as unknown as IRows;

// FormTemplate может содержать как поля документа, так и временные поля только для отчета.
const mergeDocumentValuesIntoForm = (formData: IFormData, documentFields?: IField[]): IFormData => {
    const documentFieldsByName = new Map((documentFields ?? []).map((field) => [field.name, field.value]));

    return {
        fields: formData.fields.map((field) =>
            documentFieldsByName.has(field.name)
                ? {
                      ...field,
                      value: documentFieldsByName.get(field.name),
                  }
                : field,
        ),
    };
};

export interface ICreateReportExecutorModalProps extends ICreateReportExecutorProps {
    /** Как сформировать отчёт в фоне; по умолчанию — задача `documentReport`, без фона на сервере — одним запросом. */
    reportTask?: CreateReportTask;
}

export const CreateReportExecutorModal: React.FC<ICreateReportExecutorModalProps> = (props) => {
    const { docId, actionKey, completeHandler, runInBackground = false } = props;
    const [errorText, setErrorText] = useState<string>();
    const [isBusy, setIsBusy] = useState<boolean>(true);
    // Формирование отчёта — выполнение: окно не закрыть, иначе файл скачается уже после закрытия.
    // Загрузку модели и формы бросить можно.
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const [model, setModel] = useState<ICreateReportModel>();
    const [form, setForm] = useState<IForms>();
    const [fieldsData, setFieldsData] = useState<Record<string, IFieldElem>>();
    const [hasFormTemplate, setHasFormTemplate] = useState<boolean>(false);
    const [selectedFormatOption, setSelectedFormatOption] = useState<IOption>();
    const [formatOptions, setFormatOptions] = useState<IOption[]>();

    const methods = useForm<IDocumentData>({
        mode: 'onBlur',
    });
    const { reset } = methods;
    const hasEditedFields = useUnsavedFormInput(methods);

    const service = useMemo(() => new DocumentExecutionService(props.docId), [props.docId]);

    useEffect(() => {
        let isMounted = true;

        const applyCreateReportModel = (reportModel: ICreateReportModel) => {
            setModel(reportModel);

            const reportFormats = reportModel.reportFormats;
            if (!reportFormats) {
                setFormatOptions(undefined);
                setSelectedFormatOption(undefined);
                return;
            }

            const options = Object.keys(reportFormats).map((key) => {
                return {
                    value: key,
                    label: reportFormats[key],
                };
            });

            setFormatOptions(options);
            setSelectedFormatOption(options[0]);
        };

        const fetchData = async () => {
            setIsBusy(true);
            setErrorText(undefined);

            const [modelResponse, formResponse] = await Promise.all([
                service.getCreateReportModel(props.actionKey),
                service.getFormBuilderScheme(props.actionKey),
            ]);

            if (!isMounted) return;

            applyCreateReportModel(modelResponse.data);

            const formTemplate = formResponse.data;
            setForm(formTemplate);

            if (!hasFormRows(formTemplate)) {
                setHasFormTemplate(false);
                setFieldsData(undefined);
                return;
            }

            setHasFormTemplate(true);
            const emptyFormData = getEmptyFormDataByScheme(formTemplate.view.rows.row, formTemplate.changeActions);
            const documentDataResponse = await DocumentService.getDataByAction(
                props.actionKey,
                props.docId,
                undefined,
                false,
                true,
            );

            if (!isMounted) return;

            const formData = mergeDocumentValuesIntoForm(emptyFormData, documentDataResponse.data.fields);
            setFieldsData(convertListToMap(formData.fields));
            reset(formData);
        };

        fetchData()
            .catch((error) => {
                if (isMounted) setErrorText(createReportErrorText(error));
            })
            .finally(() => {
                if (isMounted) setIsBusy(false);
            });

        return () => {
            isMounted = false;
        };
    }, [props.actionKey, props.docId, reset, service]);

    const selectedReportFormat = selectedFormatOption?.value?.toString();

    const job = useBackgroundJobRun({ kind: 'documentReport' });
    const { phase, result, startTask } = job;
    const close = useCallback(() => completeHandler?.(false), [completeHandler]);
    const reportTask = useMemo(
        () => props.reportTask ?? createBackgroundReportTask(docId, actionKey, { onDownloaded: close }),
        [props.reportTask, docId, actionKey, close],
    );

    const createReport = useCallback(
        async (formData?: IFormData) => {
            if (!model) {
                setErrorText('Не удалось получить модель данных.');
                return;
            }

            setErrorText(undefined);
            const request: ICreateReportRequest = { reportFormat: selectedReportFormat, formData };
            if (runInBackground) {
                startTask(reportTask(request));
                return;
            }

            setIsBusy(true);
            setIsDownloading(true);
            try {
                await downloadReport(docId, actionKey, request);
                close();
            } catch (reason) {
                setErrorText(createReportErrorText(reason));
            } finally {
                setIsBusy(false);
                setIsDownloading(false);
            }
        },
        [actionKey, close, docId, model, reportTask, runInBackground, selectedReportFormat, startTask],
    );

    /** Отправка идёт (проверка формы асинхронная): второе нажатие до смены фазы не ставит вторую задачу. */
    const submittingRef = useRef(false);

    const submitForm = useCallback(
        async (data: FieldValues) => {
            setErrorText(undefined);

            // Отчёт без пользовательской формы — скачиваем сразу, без formData.
            if (!hasFormTemplate) {
                await createReport();
                return;
            }

            if (!form?.view?.rows?.row || !fieldsData) {
                setErrorText('Не удалось получить схему формы отчета.');
                return;
            }

            if (form.view.validators) {
                const errors: string[] = [];
                const validatorManager = new ValidatorManager(fieldsData, methods);
                await validatorManager.validateAllAsync(form.view.validators.validator, errors);

                if (errors.length > 0) {
                    sendModalMsg({
                        message: errors,
                        type: 'error',
                    });
                    return;
                }
            }

            const formData = getFormDataByScheme(form.view.rows.row, data, false, form.changeActions);
            await createReport(formData);
        },
        [createReport, fieldsData, form, hasFormTemplate, methods],
    );

    const onSubmit = useCallback(
        async (data: FieldValues) => {
            // Подвал итога отправляет ту же форму: «Готово» закрывает окно.
            if (phase === 'result') {
                close();
                return;
            }
            if (phase !== 'idle' || submittingRef.current) return;
            submittingRef.current = true;
            try {
                await submitForm(data);
            } finally {
                submittingRef.current = false;
            }
        },
        [close, phase, submitForm],
    );

    const formatSelect = (): ReactNode => {
        return model?.isReportFormatSelectable ? (
            // Подпись у выбора вне формы отчёта: поле формы с именем format не должно показывать здесь свою ошибку
            <FormControl name="format" label="Формат отчёта" control={null}>
                <Select
                    placeholder="Формат отчёта"
                    values={selectedFormatOption ? [selectedFormatOption] : []}
                    multiselect={false}
                    options={formatOptions ?? []}
                    readOnly
                    onChange={(option) => {
                        setSelectedFormatOption(option[0]);
                    }}
                />
            </FormControl>
        ) : null;
    };

    // Один ModalFormBuilder на весь жизненный цикл (загрузка → форма / без формы).
    // Раньше загрузка рендерилась отдельным <Modal>, а с появлением шаблона формы
    // верхний элемент менялся на <ModalFormBuilder> — remount заставлял Mantine
    // повторно проиграть анимацию открытия (та самая двойная анимация).
    const contentReady = !isBusy && (hasFormTemplate ? !!fieldsData : true);
    const hasUnsavedInput = () => hasEditedFields() || selectedFormatOption?.value !== formatOptions?.[0]?.value;

    const messageBoxProp: IModalFormBuilderProps = {
        header: props.title,
        showOkButton: contentReady,
        showCancelButton: true,
        okButtonText: props.okButtonText,
        cancelButtonText: props.cancelButtonText,
        size: props.modalSize,
        loading: isBusy,
        closeDisabled: isDownloading,
        hasUnsavedInput,
        onSubmit: onSubmit,
        onCancel: close,
        errorText: errorText,
        formMethods: methods,
        fields: fieldsData ?? {},
        changeActions: form?.changeActions,
        rows: form?.view ?? EMPTY_ROWS,
        showContent: contentReady,
        docId: props.docId,
    };

    if (!runInBackground) return <ModalFormBuilder {...messageBoxProp}>{formatSelect()}</ModalFormBuilder>;

    const reportTitle = model?.reportTitle?.trim();
    const subject = reportTitle ? (
        <>
            Формируем «<b>{reportTitle}</b>»
        </>
    ) : (
        'Формируем отчёт'
    );
    const hasFields = hasFormTemplate || !!model?.isReportFormatSelectable;
    // Без полей уходить нечему: лоадер встаёт сразу, а не после ухода пустой формы.
    const stagePhase = !hasFields && phase === 'leaving' ? 'loading' : phase;
    const finished = phase === 'result' && !!result;
    const succeeded = finished && result.tone === 'ok';
    const wait = backgroundJobModalProps(job, {
        title: reportTitle || props.title,
        close,
        cancelButtonText: finished ? 'Закрыть' : props.cancelButtonText,
    });

    // Итог закрывается одной кнопкой: успех — «Готово», отказ — «Закрыть». Пока задача идёт, кнопки действия нет.
    const backgroundProps: Partial<IModalFormBuilderProps> = {
        ...wait,
        // Окно без полей — вопрос, лоадер и итог — одного размера; иначе ширина прыгала бы с телом окна.
        size: contentReady && !hasFields ? 'm' : props.modalSize,
        showOkButton: contentReady && (phase === 'idle' || succeeded),
        showCancelButton: !succeeded,
        okButtonText: finished ? 'Готово' : props.okButtonText,
        closeDisabled: isDownloading || wait.closeDisabled,
        hasUnsavedInput: () => phase === 'idle' && hasUnsavedInput(),
        // Отчёт без формы и выбора формата: до старта тела у окна нет, окно ожидания встаёт с нажатием «Сформировать».
        renderContent:
            phase === 'idle' && !hasFields
                ? undefined
                : (content) => (
                      <BackgroundJobWait
                          ref={job.waitRef}
                          kind="documentReport"
                          phase={stagePhase}
                          subject={subject}
                          stageText={job.stage}
                          stageName={job.stageName}
                          background={job.background}
                          result={result}
                          onDownload={job.downloadResultFile}
                          downloading={job.downloadingResultFile}
                      >
                          {content}
                      </BackgroundJobWait>
                  ),
    };

    return (
        <ModalFormBuilder {...messageBoxProp} {...backgroundProps}>
            {formatSelect()}
        </ModalFormBuilder>
    );
};
